import type { JSX } from 'react'

import { describeMethod, formatMoney } from '../lib/format.ts'
import type { PaymentDecline, PaymentResult } from '../lib/types.ts'

export type PaymentOutcomeProps =
  | { kind: 'recovering' }
  | { kind: 'unknown'; message: string }
  | { kind: 'declined'; decline: PaymentDecline; onChangeCard: () => void }
  | { kind: 'paid'; result: PaymentResult; locale: string }

function attemptsLeft(count: number): string {
  if (count === 0) return 'This session allows no more payment attempts.'
  return count === 1 ? '1 attempt left.' : `${count} attempts left.`
}

/**
 * Shows what happened to a submitted payment: checking, declined, unknown, or paid.
 * @param props - The outcome and what that outcome needs to show.
 * @returns The outcome screen.
 */
export function PaymentOutcome(props: PaymentOutcomeProps): JSX.Element {
  switch (props.kind) {
    case 'recovering':
      return (
        <p
          role="status"
          data-testid="payment-recovering"
          className="blytz-alert blytz-alert--warning"
        >
          {'Checking the payment. Do not close this window.'}
        </p>
      )
    case 'unknown':
      return (
        <p
          role="alert"
          data-testid="payment-unknown"
          className="blytz-alert blytz-alert--danger"
        >
          {props.message}
        </p>
      )
    case 'declined': {
      const { decline, onChangeCard } = props
      return (
        <div className="flex flex-col gap-3">
          <div
            role="alert"
            data-testid="payment-declined"
            className="blytz-alert blytz-alert--danger flex flex-col gap-1"
          >
            <p data-testid="payment-decline-reason">{decline.reason}</p>
            <p data-testid="payment-attempts-left">
              {attemptsLeft(decline.attemptsRemaining)}
            </p>
          </div>
          {decline.attemptsRemaining > 0 && (
            <button
              type="button"
              data-testid="payment-change-card"
              className="blytz-btn blytz-btn--primary blytz-btn--full"
              onClick={onChangeCard}
            >
              {'Use another card'}
            </button>
          )}
        </div>
      )
    }
    default: {
      const { result, locale } = props
      const card = describeMethod({ ...result, paymentType: 'card' })
      return (
        <p
          data-testid="terminal-success"
          className="blytz-alert blytz-alert--success"
        >
          {`${result.status === 'paid' ? 'Paid' : 'Payment pending:'} ${formatMoney(result.total, result.currency, locale)} with ${card}`}
        </p>
      )
    }
  }
}
